import React, { useEffect, useRef, useState } from "react";
// NOTE: adjust nothing here — this component is self-contained.

/**
 * SmartHero — a fixed-height letterbox hero (default 200px) that decides
 * WHERE to crop from based on the image itself.
 *
 * How: the image is drawn onto a tiny offscreen canvas (96px wide), per-row
 * gradient energy is computed (edges/detail = interesting), smoothed, given
 * a mild centre bias, and the sliding window matching the visible band is
 * placed over the peak. The result becomes `object-position: 50% Y%`.
 * Validated against real photos: on a beach shot with a large empty sky it
 * selects the village/waterline strip (Y≈29%) instead of the sky a naive
 * centre crop would show.
 *
 * Fallbacks, in order:
 *  - canvas blocked by CORS (probe uses crossOrigin="anonymous"; Firebase
 *    Storage download URLs send ACAO:* so this normally succeeds) → Y=45%
 *  - probe fails entirely → the visible <img> still loads and shows at 45%
 *
 * The visible <img> is independent of the probe, so the hero can never go
 * blank because of the analysis. When the computed position arrives after
 * first paint, the crop glides there once (object-position transition);
 * prefers-reduced-motion disables the glide globally.
 *
 * NOTE on image protection: onContextMenu/draggable below are friction
 * only (deter casual right-click-save / drag-save). They do not stop
 * DevTools, View Source, or screenshots — there is no web API that can
 * black out a screenshot. Treat this as a UX nudge, not a security
 * control. This does NOT touch the crossOrigin="anonymous" probe above,
 * which is required for the canvas crop analysis and is unrelated to
 * download protection.
 */

const PROBE_W = 96;
const FALLBACK_Y = 45; // slightly above centre suits horizons

function computeFocusY(probe, bandFrac) {
  const w = PROBE_W;
  const h = Math.max(8, Math.round((probe.naturalHeight / probe.naturalWidth) * w));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(probe, 0, 0, w, h);
  const d = ctx.getImageData(0, 0, w, h).data; // throws if canvas is tainted

  // Grayscale
  const g = new Float32Array(w * h);
  for (let i = 0; i < w * h; i++) {
    const p = i * 4;
    g[i] = 0.299 * d[p] + 0.587 * d[p + 1] + 0.114 * d[p + 2];
  }

  // Per-row gradient energy (|dx| + |dy|)
  const row = new Float32Array(h);
  for (let y = 0; y < h; y++) {
    let s = 0;
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (x + 1 < w) s += Math.abs(g[i + 1] - g[i]);
      if (y + 1 < h) s += Math.abs(g[i + w] - g[i]);
    }
    row[y] = s;
  }

  // Smooth (box, radius 2)
  const sm = new Float32Array(h);
  for (let y = 0; y < h; y++) {
    let s = 0;
    let n = 0;
    for (let k = -2; k <= 2; k++) {
      const yy = y + k;
      if (yy >= 0 && yy < h) {
        s += row[yy];
        n++;
      }
    }
    sm[y] = s / n;
  }

  // Mild centre prior so uniformly-busy texture doesn't drift to an edge
  const mid = (h - 1) / 2;
  for (let y = 0; y < h; y++) sm[y] *= 1 - 0.35 * (Math.abs(y - mid) / mid);

  // Best window of the band's height
  const band = Math.min(h, Math.max(3, Math.round(h * bandFrac)));
  if (band >= h) return 50;
  let cur = 0;
  for (let y = 0; y < band; y++) cur += sm[y];
  let best = cur;
  let bestTop = 0;
  for (let top = 1; top <= h - band; top++) {
    cur += sm[top + band - 1] - sm[top - 1];
    if (cur > best) {
      best = cur;
      bestTop = top;
    }
  }
  return (bestTop / (h - band)) * 100;
}

export default function SmartHero({ src, alt = "", height = 200 }) {
  const frameRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [posY, setPosY] = useState(FALLBACK_Y);

  useEffect(() => {
    let alive = true;
    setPosY(FALLBACK_Y);

    const probe = new Image();
    probe.crossOrigin = "anonymous";
    probe.onload = () => {
      if (!alive) return;
      try {
        const frameW = frameRef.current ? frameRef.current.clientWidth : 1200;
        // Fraction of the image's height visible in the band under object-fit: cover
        const bandFrac = Math.min(
          1,
          (height * probe.naturalWidth) / (frameW * probe.naturalHeight)
        );
        const y = computeFocusY(probe, bandFrac);
        if (alive) setPosY(Math.max(0, Math.min(100, y)));
      } catch {
        /* tainted canvas or decode issue — keep the fallback position */
      }
    };
    probe.src = src;

    return () => {
      alive = false;
    };
  }, [src, height]);

  return (
    <div
      ref={frameRef}
      className="smart-hero"
      style={{ height }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        className={ready ? "is-ready" : ""}
        style={{ objectPosition: `50% ${posY}%` }}
        onLoad={() => setReady(true)}
      />
    </div>
  );
}